import type React from "react"
import { getScope, normalizarId } from "../hooks/useScope"
import { useState, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useIsMobile } from "../hooks/useMediaQuery"
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  TableSortLabel,
} from "@mui/material"
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import api from "../services/api"
import type { AxiosError } from "axios"
import HorseLoader from "./loadingHorse"
import { useCryptoNavigation, useAppNavigation } from "../App"

// Interface para o usuário
interface UserData {
  userId: number
  Nome: string
  Role: string
}

// Interface para a coluna
interface Column {
  id: string
  label: string
  minWidth?: number
  align?: "right" | "left" | "center"
  format?: (value: any, row?: any) => React.ReactNode
  alternativeField?: string
  /** Oculta a coluna (ex.: `hide: isMobile`). Aplicado na tabela e nos cards. */
  hide?: boolean
  /** Valor usado para ORDENAR (quando difere do exibido). Ex.: ordenar a coluna
   *  ROI pela contagem de pontos, não pela string JSON. */
  sortValue?: (row: any) => any
  /** No card mobile, omite o rótulo e mostra só o valor (ex.: uma badge que já
   *  se explica sozinha, como o perfil do usuário). */
  mobileHideLabel?: boolean
}

// ✅ NOVO: Tipos de entidade para controle de permissões
type EntityType = 'horses' | 'users' | 'partners' | 'tenants' | 'other'

/** Valor reservado para "sem vínculo" — distinto de "todos". */
const SEM_VINCULO = "__sem__"

export interface FiltroCampo {
  /**
   * Campo da LINHA que guarda o id. Use o nome da própria entidade
   * (`userPartner`, `tenantPartnerId`, …): não há reserva cruzada.
   */
  id: string
  /** Rótulo legível da linha. Sem ele, o seletor mostra o id. */
  rotulo?: (row: any) => string | null | undefined
  /** Texto do seletor. */
  label: string
  /**
   * O filtro só aparece quando o campo correspondente do usuário logado está
   * VAZIO. Quem já pertence a um parceiro não precisa filtrar por parceiro —
   * tudo que ele vê já é do dele.
   *
   * VAZIO é `null`. ZERO não é vazio: é o grupo raiz / o proprietário zero.
   */
  exigeVazio: "partner" | "tenant"
}

interface EntityListProps {
  title: string
  endpoint: string
  columns: Column[]
  addPath: string
  editPathPrefix: string
  idField?: string
  disableActions?: boolean
  dataPath?: string | null
  debug?: boolean
  data?: any[]
  disableAutoFetch?: boolean
  loading?: boolean
  refreshTrigger?: number
  onRowClick?: (row: any) => void
  transformData?: (data: any[]) => any[] | Promise<any[]>
  emptyMessage?: string
  addButtonLabel?: string
  disableAddButton?: boolean
  containerProps?: any
  customActions?: (row: any) => React.ReactNode
  deleteConfirmationMessage?: string
  extraParams?: Record<string, any>
  hideViewAction?: boolean
  useEncryptedNavigation?: boolean
  encryptedPageName?: string
  onAddClick?: () => void
  onEditClick?: (id: number | string) => void
  onDeleteClick?: (id: number) => void
  /** Bloqueia a exclusão de uma linha específica: retorne a mensagem de
   *  impedimento (mostrada ao usuário) ou null para permitir. */
  getRowDeleteBlockReason?: (row: any) => string | null
  showDeleteButton?: boolean
  hideDeleteButton?: boolean
  disableDelete?: boolean
  canDelete?: boolean
  deleteDisabled?: boolean
  // ✅ NOVO: Prop para identificar o tipo de entidade
  entityType?: EntityType
  extraHeaderActions?: React.ReactNode
  /** Filtros oferecidos a quem administra vários grupos. Ver FiltroCampo. */
  filterFields?: FiltroCampo[]
}

const EntityList: React.FC<EntityListProps> = ({
  title,
  endpoint,
  columns,
  addPath,
  editPathPrefix,
  idField = "id",
  disableActions = false,
  dataPath = null,
  debug = false,
  data: providedData,
  disableAutoFetch = false,
  refreshTrigger = 0,
  onRowClick,
  transformData,
  emptyMessage,
  addButtonLabel,
  disableAddButton = false,
  containerProps,
  customActions,
  deleteConfirmationMessage,
  extraParams,
  useEncryptedNavigation = false,
  encryptedPageName,
  onAddClick,
  onEditClick,
  getRowDeleteBlockReason,
  entityType = 'other', // ✅ NOVO: Valor padrão
  extraHeaderActions,
  filterFields,
}) => {
  const { t } = useTranslation()
  const [data, setData] = useState<any[]>(providedData || [])
  const [page, setPage] = useState(0)
  const storageKey = `rowsPerPage:${endpoint ?? "default"}`
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem(storageKey)
    return saved ? parseInt(saved, 10) : 25
  })
  const [loading, setLoading] = useState(!providedData)
  const [error, setError] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<number | null>(null)
  const [rawResponse, setRawResponse] = useState<any>(null)
  const [refresh, setRefresh] = useState(refreshTrigger)
  // Card x tabela: alinhado ao mesmo breakpoint (600px) que as páginas usam para
  // decidir `hide: isMobile` nas colunas, evitando descompasso.
  const isMobile = useIsMobile()
  // Persiste a ordenação por lista (mesma estratégia do rowsPerPage acima), para
  // não voltar ao default quando a lista remonta — ex.: ao abrir/fechar um modal.
  const sortStorageKey = `sort:${endpoint ?? "default"}`
  const [orderBy, setOrderBy] = useState<string>(() => {
    try {
      return JSON.parse(localStorage.getItem(sortStorageKey) || "{}").orderBy || ""
    } catch {
      return ""
    }
  })
  const [order, setOrder] = useState<"asc" | "desc">(() => {
    try {
      return JSON.parse(localStorage.getItem(sortStorageKey) || "{}").order === "desc"
        ? "desc"
        : "asc"
    } catch {
      return "asc"
    }
  })

  const navigate = useNavigate()

  // Hooks de navegação criptografada (se disponível)
  const cryptoNavigation = useCryptoNavigation()
  const appNavigation = useAppNavigation()

  // ✅ CORRIGIDO: Função para obter role, ID e tenant do usuário
  const getUserData = (): { role: string; userId: number; userTenant: number | null } => {
    try {
      const userStr = localStorage.getItem("user")
      
      if (userStr) {
        const userData = JSON.parse(userStr) as any
        const role = userData.Role || "Viewer"
        const userId = userData.userId || 0
        // `userData.userTenant || null` colapsava o ZERO em null, e zero agora
        // é um proprietário de verdade (é o que governa o cadastro de
        // parceiros). Sem este cuidado, o admin do proprietário zero seria
        // lido como "sem proprietário" e receberia acesso total.
        const bruto = userData.userTenant
        const userTenant =
          bruto === null || bruto === undefined || bruto === ''
            ? null
            : Number(bruto)
        console.log("🔍 DEBUG - userData completo:", userData)
        console.log("🔍 DEBUG - role extraído:", role, "userId:", userId, "userTenant:", userTenant)
        return { role, userId, userTenant }
      }
    } catch (error) {
      console.error("❌ Erro ao obter dados do usuário:", error)
    }
    return { role: "Viewer", userId: 0, userTenant: null } // Valores padrão se houver erro
  }

  const { role: userRole, userId, userTenant } = getUserData()
  console.log("🎯 DEBUG - userRole final:", userRole, "userId:", userId, "userTenant:", userTenant, "entityType:", entityType)

  // ✅ CORRIGIDO: Sistema de permissões baseado em role, tenant associado e tipo de entidade
  const checkPermissions = (action: 'view' | 'add' | 'edit' | 'delete') => {
    const role = userRole.toLowerCase().trim()
    
    console.log(`🔒 DEBUG - Verificando permissão: action=${action}, role=${role}, userId=${userId}, userTenant=${userTenant}, entityType=${entityType}`)
    
    // Definir roles de apenas visualização
    const viewOnlyRoles = ['viewer', 'alert', 'manager']
    
    // Parceiro tem regras próprias, decididas pelo resolvedor de escopo —
    // criar/editar mora no proprietário zero, excluir exige não ter
    // proprietário associado.
    if (entityType === 'partners') {
      const scope = getScope()
      if (action === 'view') return scope.canManagePartners
      if (action === 'delete') return scope.canDeletePartners
      return scope.canManagePartners
    }

    // ✅ LÓGICA CORRIGIDA: Se role for "admin", verificar se tem tenant associado
    if (role === 'admin') {
      // Admin SEM tenant associado = Acesso total (como SuperAdmin)
      if (userTenant === null || userTenant === undefined) {
        console.log("✅ Admin sem tenant associado - Acesso total liberado para:", action)
        return true
      }
      
      // Admin COM tenant associado = Acesso limitado
      console.log("⚠️ Admin com tenant associado - Verificando acesso limitado")
      
      // Admin com tenant pode visualizar tudo
      if (action === 'view') {
        console.log("✅ Admin com tenant - Visualização liberada")
        return true
      }
      
      // Admin com tenant pode adicionar/editar/excluir apenas cavalos e usuários
      if (['add', 'edit', 'delete'].includes(action)) {
        const hasPermission = entityType === 'horses' || entityType === 'users'
        console.log(`${hasPermission ? '✅' : '❌'} Admin com tenant - ${action} em ${entityType}: ${hasPermission}`)
        return hasPermission
      }
    }
    
    // Roles de apenas visualização (Viewer, Alert, Manager)
    if (viewOnlyRoles.includes(role)) {
      const hasPermission = action === 'view'
      console.log(`${hasPermission ? '✅' : '❌'} ${role} - ${action}: ${hasPermission}`)
      return hasPermission
    }
    
    // Outros roles: comportamento restritivo por padrão (apenas visualização)
    const hasPermission = action === 'view'
    console.log(`${hasPermission ? '✅' : '❌'} Role desconhecido (${role}) - ${action}: ${hasPermission}`)
    return hasPermission
  }

  // ✅ NOVO: Verificações específicas de permissão
  const canAdd = checkPermissions('add')
  const canEdit = checkPermissions('edit')
  const canDelete = checkPermissions('delete')
  const canView = checkPermissions('view')

  console.log("🔑 DEBUG - Permissões finais:", { canAdd, canEdit, canDelete, canView })

  // Efeito para atualizar os dados quando providedData mudar
  useEffect(() => {
    if (providedData) {
      if (transformData) {
        const applyTransform = async () => {
          try {
            const result = transformData(providedData)
            if (result instanceof Promise) {
              const processedData = await result
              setData(processedData)
            } else {
              setData(result)
            }
          } catch (error) {
            console.error("Erro ao transformar dados fornecidos:", error)
            setData(providedData)
          }
        }
        applyTransform()
      } else {
        setData(providedData)
      }
      setLoading(false)
    }
  }, [providedData, transformData])

  useEffect(() => {
    setRefresh(refreshTrigger)
  }, [refreshTrigger])

  useEffect(() => {
    if (!disableAutoFetch) {
      fetchData()
    }
  }, [refresh, endpoint, disableAutoFetch])

  useEffect(() => {
    if (!disableAutoFetch && extraParams) {
      fetchData()
    }
  }, [extraParams])

  const fetchData = async () => {
    if (providedData) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem("token")
      const userStr = localStorage.getItem("user")

      if (!token || !userStr) {
        setError(t("entity.authRequired"))
        setLoading(false)
        return
      }

      let userId: number | null = null
      try {
        const userData = JSON.parse(userStr) as UserData
        userId = userData.userId
      } catch (parseError) {
        console.error("Erro ao analisar dados do usuário:", parseError)
      }

      console.log(`Fetching data from endpoint: ${endpoint}`, { userId, extraParams })

      const params = {
        ...(userId ? { userId } : {}),
        ...(extraParams || {}),
      }

      const requestConfig = Object.keys(params).length > 0 ? { params } : undefined
      const response = await api.get(endpoint, requestConfig)

      setRawResponse(response.data)
      console.log(`Data received from ${endpoint}:`, response.data)

      let responseData

      if (dataPath) {
        responseData = response.data[dataPath]
        console.log(`Extracted data from path '${dataPath}':`, responseData)
      } else {
        if (!Array.isArray(response.data) && typeof response.data === "object" && response.data !== null) {
          const keys = Object.keys(response.data)
          if (keys.length === 1 && Array.isArray(response.data[keys[0]])) {
            const autoDetectedPath = keys[0]
            responseData = response.data[autoDetectedPath]
            console.log(`Auto-detected array data in path '${autoDetectedPath}':`, responseData)
          } else {
            responseData = response.data
          }
        } else {
          responseData = response.data
        }
      }

      if (Array.isArray(responseData)) {
        if (transformData) {
          try {
            const result = transformData(responseData)
            if (result instanceof Promise) {
              const processedData = await result
              setData(processedData)
            } else {
              setData(result)
            }
          } catch (error) {
            console.error("Erro ao transformar dados:", error)
            setData(responseData)
          }
        } else {
          setData(responseData)
        }
      } else if (responseData && typeof responseData === "object") {
        const objToArray = Object.values(responseData)
        if (Array.isArray(objToArray[0])) {
          if (transformData) {
            try {
              const result = transformData(objToArray[0])
              if (result instanceof Promise) {
                const processedData = await result
                setData(processedData)
              } else {
                setData(result)
              }
            } catch (error) {
              console.error("Erro ao transformar dados:", error)
              setData(objToArray[0])
            }
          } else {
            setData(objToArray[0])
          }
          console.log("Converted object to array:", objToArray[0])
        } else {
          setData([])
          setError(t("entity.notArrayFormat"))
        }
      } else {
        setData([])
        setError(t("entity.notArrayFormat"))
      }
    } catch (error) {
      console.error("Error fetching data:", error)

      const axiosError = error as AxiosError

      if (axiosError.response) {
        console.error("Error response:", axiosError.response.status, axiosError.response.data)

        if (axiosError.response.status === 401) {
          setError(t("entity.sessionExpired"))
        } else if (axiosError.response.status === 403) {
          setError(t("entity.noPermission"))
        } else if (axiosError.response.status === 404) {
          setError(t("entity.endpointNotFound", { endpoint }))
        } else {
          const responseData = axiosError.response.data as any
          setError(`Erro ao carregar dados: ${responseData?.message || "Erro desconhecido"}`)
        }
      } else if (axiosError.request) {
        console.error("Error request:", axiosError.request)
        setError(t("entity.serverError"))
      } else {
        console.error("Error message:", axiosError.message)
        setError(t("entity.loadError"))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    setRefresh((prev) => prev + 1)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = +event.target.value
    setRowsPerPage(value)
    localStorage.setItem(storageKey, String(value))
    setPage(0)
  }

  const handleEdit = (id: number, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation()
    }

    // ✅ MELHORADO: Verificar permissão de edição
    if (!canEdit) {
      console.log(`Ação de edição bloqueada para usuário com role: ${userRole} na entidade: ${entityType}`)
      return
    }

    if (onEditClick) {
      onEditClick(id)
      return
    }

    if (useEncryptedNavigation && cryptoNavigation && encryptedPageName) {
      cryptoNavigation.navigateToPage(encryptedPageName, { 
        mode: 'edit', 
        id: String(id),
        ...(extraParams || {})
      })
      return
    }

    navigate(`${editPathPrefix}/${id}`)
  }

  const handleAdd = () => {
    // ✅ MELHORADO: Verificar permissão de adição
    if (!canAdd) {
      console.log(`Ação de adicionar bloqueada para usuário com role: ${userRole} na entidade: ${entityType}`)
      return
    }

    if (onAddClick) {
      onAddClick()
      return
    }

    if (useEncryptedNavigation && cryptoNavigation && encryptedPageName) {
      cryptoNavigation.navigateToPage(encryptedPageName, { 
        mode: 'new',
        ...(extraParams || {})
      })
      return
    }

    navigate(addPath)
  }

  const handleDeleteClick = (id: number, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation()
    }

    // ✅ MELHORADO: Verificar permissão de exclusão
    if (!canDelete) {
      console.log(`Ação de excluir bloqueada para usuário com role: ${userRole} na entidade: ${entityType}`)
      return
    }

    // Bloqueio por linha (ex.: baia com cavalo/câmera associados).
    if (getRowDeleteBlockReason) {
      const row = data.find((r) => getIdFromRow(r) === id)
      const reason = row ? getRowDeleteBlockReason(row) : null
      if (reason) {
        window.alert(reason)
        return
      }
    }

    setItemToDelete(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (itemToDelete === null) return

    try {
      await api.delete(`${endpoint}/${itemToDelete}`)

      console.log(`Item ${itemToDelete} deleted successfully`)
      if (!disableAutoFetch) {
        setRefresh((prev) => prev + 1)
      } else {
        setData((prevData) => prevData.filter((item) => getIdFromRow(item) !== itemToDelete))
      }
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    } catch (error) {
      console.error("Error deleting item:", error)

      const axiosError = error as AxiosError

      if (axiosError.response) {
        console.error("Delete error response:", axiosError.response.status, axiosError.response.data)
        setError(`Erro ao excluir: ${(axiosError.response.data as any)?.message || "Erro desconhecido"}`)
      } else {
        setError(t("entity.deleteError"))
      }
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setItemToDelete(null)
  }

  const handleRowClick = (row: any) => {
    if (onRowClick && canView) {
      onRowClick(row)
    }
  }

  const getValueFromRow = (row: any, column: Column): any => {
    const primaryValue = row[column.id]

    if ((primaryValue === undefined || primaryValue === null) && column.alternativeField) {
      return row[column.alternativeField]
    }

    return primaryValue
  }

  const getIdFromRow = (row: any): number => {
    if (idField.includes(".")) {
      const parts = idField.split(".")
      let value = row
      for (const part of parts) {
        if (value === null || value === undefined) return -1
        value = value[part]
      }
      return value
    }

    const potentialIdFields = [idField, "id", "_id", "ID", "Id"]

    const endpointBase = endpoint.split("/").pop() || ""
    potentialIdFields.push(`${endpointBase}_id`)
    potentialIdFields.push(`${endpointBase}Id`)

    for (const field of potentialIdFields) {
      if (row[field] !== undefined && row[field] !== null) {
        return row[field]
      }
    }

    return -1
  }

  const handleSort = (columnId: string) => {
  const isAsc = orderBy === columnId && order === "asc"
  const newOrder = isAsc ? "desc" : "asc"
  setOrder(newOrder)
  setOrderBy(columnId)
  setPage(0)
  try {
    localStorage.setItem(sortStorageKey, JSON.stringify({ orderBy: columnId, order: newOrder }))
  } catch {
    /* storage indisponível — ignora */
  }
}

const normalizeValue = (value: any) => {
  if (value === null || value === undefined) return ""

  if (typeof value === "number") return value

  if (typeof value === "string") {
    const dateValue = Date.parse(value)
    if (!Number.isNaN(dateValue) && value.match(/\d{4}-\d{2}-\d{2}/)) {
      return dateValue
    }

    return value.toLowerCase()
  }

  return String(value).toLowerCase()
}

// ── Filtros por grupo ────────────────────────────────────────────────────
//
// Quem administra vários grupos não tem como conferir "todos daquele mesmo
// grupo" só ordenando. A regra: cada um filtra pelo que NÃO tem preenchido.
// Um admin sem parceiro e sem proprietário filtra pelos dois; um que pertence
// a um parceiro mas não a um proprietário filtra só por proprietário — para
// ele, tudo que aparece já é do seu parceiro.
//
// VAZIO é `null`. ZERO é valor de verdade (grupo raiz / proprietário zero), e
// quem o tem preenchido não ganha o filtro.
const [selecaoFiltro, setSelecaoFiltro] = useState<Record<string, string>>({})

const filtrosAplicaveis = useMemo(() => {
  const escopo = getScope()
  // Só admin (1) e gerente (2) administram mais de um grupo.
  if (escopo.userRole !== 1 && escopo.userRole !== 2) return []
  return (filterFields ?? []).filter((f) =>
    f.exigeVazio === "partner"
      ? escopo.partnerId === null
      : escopo.tenantId === null,
  )
}, [filterFields])

/** Valor do filtro para uma linha. NULO vira "sem vínculo"; ZERO é valor. */
const valorDoFiltro = (row: any, f: FiltroCampo) => {
  const id = normalizarId(row?.[f.id])
  return id === null ? SEM_VINCULO : String(id)
}

const montarOpcoes = (linhas: any[], f: FiltroCampo) => {
  const mapa = new Map<string, string>()
  for (const row of linhas) {
    const valor = valorDoFiltro(row, f)
    if (mapa.has(valor)) continue
    const nome = f.rotulo?.(row)?.toString().trim()
    mapa.set(
      valor,
      valor === SEM_VINCULO ? t("common.unlinked") : nome || `#${valor}`,
    )
  }
  const todas = [...mapa.entries()].map(([valor, rotulo]) => ({ valor, rotulo }))

  // "Sem vínculo" não é um NOME — é a ausência de um. Ordenado junto com os
  // demais, ele caía no meio da lista (sob a letra S, em português) e sumia
  // entre os nomes reais. Vai para o fim, separado, como opção à parte.
  const semVinculo = todas.filter((o) => o.valor === SEM_VINCULO)
  const nomeados = todas
    .filter((o) => o.valor !== SEM_VINCULO)
    .sort((a, b) => a.rotulo.localeCompare(b.rotulo, undefined, { numeric: true }))

  return [...nomeados, ...semVinculo]
}

// QUAIS filtros aparecem sai da lista INTEIRA, de propósito: se dependesse do
// recorte atual, um filtro sumiria da tela no meio do uso. Um filtro com um
// valor só não recorta nada, então não aparece.
const filtrosVisiveis = useMemo(
  () => filtrosAplicaveis.filter((f) => montarOpcoes(data, f).length > 1),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [data, filtrosAplicaveis, t],
)

// Já as OPÇÕES de cada filtro saem do que os OUTROS filtros deixaram passar.
// Sem isto, escolher um parceiro ainda oferecia proprietários de todos os
// parceiros — dois recortes que não conversam.
const opcoesFiltro = useMemo(() => {
  const saida: Record<string, { valor: string; rotulo: string }[]> = {}
  for (const f of filtrosVisiveis) {
    const outros = filtrosVisiveis.filter(
      (o) => o.id !== f.id && selecaoFiltro[o.id],
    )
    const base =
      outros.length === 0
        ? data
        : data.filter((row) =>
            outros.every((o) => valorDoFiltro(row, o) === selecaoFiltro[o.id]),
          )
    saida[f.id] = montarOpcoes(base, f)
  }
  return saida
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [data, filtrosVisiveis, selecaoFiltro, t])

// Uma escolha pode deixar de existir depois que o outro filtro recortou —
// mantê-la selecionada esvaziaria a lista sem explicação.
useEffect(() => {
  const obsoletos = filtrosVisiveis.filter(
    (f) =>
      selecaoFiltro[f.id] &&
      !(opcoesFiltro[f.id] ?? []).some((op) => op.valor === selecaoFiltro[f.id]),
  )
  if (obsoletos.length === 0) return
  setSelecaoFiltro((prev) => {
    const copia = { ...prev }
    for (const f of obsoletos) delete copia[f.id]
    return copia
  })
}, [filtrosVisiveis, opcoesFiltro, selecaoFiltro])

const dadosFiltrados = useMemo(() => {
  const ativos = filtrosVisiveis.filter((f) => selecaoFiltro[f.id])
  if (ativos.length === 0) return data
  return data.filter((row) =>
    ativos.every((f) => valorDoFiltro(row, f) === selecaoFiltro[f.id]),
  )
}, [data, selecaoFiltro, filtrosVisiveis])

const sortedData = useMemo(() => {
  if (!orderBy) return dadosFiltrados

  const column = columns.find((col) => col.id === orderBy)
  if (!column) return dadosFiltrados

  return [...dadosFiltrados].sort((a, b) => {
    // sortValue customizado tem prioridade (ex.: ROI ordena por nº de pontos).
    const rawA = column.sortValue ? column.sortValue(a) : getValueFromRow(a, column)
    const rawB = column.sortValue ? column.sortValue(b) : getValueFromRow(b, column)
    const valueA = normalizeValue(rawA)
    const valueB = normalizeValue(rawB)

    const dir = order === "asc" ? 1 : -1

    if (typeof valueA === "number" && typeof valueB === "number") {
      return (valueA - valueB) * dir
    }

    return String(valueA).localeCompare(String(valueB), undefined, { numeric: true, sensitivity: "base" }) * dir
  })
}, [dadosFiltrados, orderBy, order, columns])

  // Colunas efetivamente visíveis (respeita `hide`, que antes era ignorado).
  const visibleColumns = useMemo(() => columns.filter((c) => !c.hide), [columns])

  const containerComponent = (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <Typography variant="h5" component="h1" sx={{ mb: isMobile ? 2 : 0 }}>
          {title}
        </Typography>

        <Box>
          {!disableAutoFetch && (
            <Tooltip title={t("common.refresh")}>
              <IconButton onClick={handleRefresh} sx={{ mr: 1 }} color="primary" disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* ✅ MELHORADO: Mostrar botão de adicionar baseado nas permissões */}
          {extraHeaderActions && (
            <Box component="span" sx={{ mr: 1 }}>{extraHeaderActions}</Box>
          )}

          {!disableAddButton && canAdd && (
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAdd}>
              {addButtonLabel || t("common.add")}
            </Button>
          )}
        </Box>
      </Box>

      {/* Filtros por grupo. Só aparecem para quem realmente administra vários
          (ver filtrosAplicaveis) e quando há mais de um valor a escolher. */}
      {filtrosVisiveis.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            flexWrap: "wrap",
          }}
        >
          {filtrosVisiveis.map((f) => (
              <FormControl
                key={f.id}
                size="small"
                sx={{ minWidth: isMobile ? "100%" : 200 }}
              >
                <InputLabel id={`filtro-${f.id}`}>{f.label}</InputLabel>
                <Select
                  labelId={`filtro-${f.id}`}
                  label={f.label}
                  value={selecaoFiltro[f.id] ?? ""}
                  onChange={(e) => {
                    const valor = String(e.target.value)
                    setSelecaoFiltro((prev) => ({ ...prev, [f.id]: valor }))
                    // Um recorte novo pode deixar a página atual fora da
                    // lista; voltar ao início evita a tela vazia enganosa.
                    setPage(0)
                  }}
                >
                  <MenuItem value="">
                    <em>{t("common.all")}</em>
                  </MenuItem>
                  <Divider />
                  {(opcoesFiltro[f.id] ?? []).map((op) =>
                    op.valor === SEM_VINCULO
                      ? [
                          <Divider key={`${op.valor}-sep`} />,
                          <MenuItem key={op.valor} value={op.valor}>
                            <em>{op.rotulo}</em>
                          </MenuItem>,
                        ]
                      : (
                          <MenuItem key={op.valor} value={op.valor}>
                            {op.rotulo}
                          </MenuItem>
                        ),
                  )}
                </Select>
              </FormControl>
            ))}

          {Object.values(selecaoFiltro).some(Boolean) && (
            <Button
              size="small"
              onClick={() => {
                setSelecaoFiltro({})
                setPage(0)
              }}
            >
              {t("common.clearFilters")}
            </Button>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
            <HorseLoader />
          </Box>
        ) : (
          <>
            {isMobile ? (
              <Box sx={{ mt: 2 }}>
                {data.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography color="text.secondary">{emptyMessage || t("entity.emptyMessage")}</Typography>
                  </Box>
                ) : (
                  sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const [heroCol, ...restCols] = visibleColumns
                    const heroValue = heroCol ? getValueFromRow(row, heroCol) : undefined
                    return (
                      <Paper
                        key={getIdFromRow(row)}
                        variant="outlined"
                        sx={{
                          p: 2,
                          mb: 1.5,
                          borderRadius: 2,
                          cursor: onRowClick && canView ? "pointer" : "default",
                          transition: "border-color .15s ease",
                          "&:hover": onRowClick && canView ? { borderColor: "primary.main" } : {},
                        }}
                        onClick={() => handleRowClick(row)}
                      >
                        {/* Cabeçalho: campo principal em destaque + ações */}
                        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                          <Box sx={{ minWidth: 0, fontWeight: 600 }}>
                            {heroCol && (heroCol.format && heroValue !== undefined
                              ? heroCol.format(heroValue, row)
                              : <Typography sx={{ fontWeight: 600, wordBreak: "break-word" }}>{heroValue}</Typography>)}
                          </Box>
                          {(canView || canEdit || canDelete) && (
                            <Box sx={{ display: "flex", flexShrink: 0, mr: -1 }} onClick={(e) => e.stopPropagation()}>
                              {!customActions ? (
                                <>
                                  {canView && (
                                    <Tooltip title={t("common.details")}>
                                      <IconButton color="info" size="small" onClick={(e) => { e.stopPropagation(); if (onRowClick) onRowClick(row) }}>
                                        <InfoIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {canEdit && (
                                    <Tooltip title={t("common.edit")}>
                                      <IconButton color="primary" size="small" onClick={(e) => handleEdit(getIdFromRow(row), e)}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {canDelete && (
                                    <Tooltip title={t("common.delete")}>
                                      <IconButton color="error" size="small" onClick={(e) => handleDeleteClick(getIdFromRow(row), e)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                customActions(row)
                              )}
                            </Box>
                          )}
                        </Box>

                        {restCols.length > 0 && (
                          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {/* Badges/valores auto-explicativos: agrupados numa linha
                                (sem rótulo), em vez de jogados no canto oposto. */}
                            {restCols.some((c) => c.mobileHideLabel) && (
                              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                                {restCols
                                  .filter((c) => c.mobileHideLabel)
                                  .map((column) => {
                                    const value = getValueFromRow(row, column)
                                    return (
                                      <Box key={column.id}>
                                        {column.format && value !== undefined ? column.format(value, row) : value}
                                      </Box>
                                    )
                                  })}
                              </Box>
                            )}
                            {/* Pares rótulo/valor: na MESMA linha, juntos à esquerda
                                (não mais um em cada canto via space-between). */}
                            {restCols
                              .filter((c) => !c.mobileHideLabel)
                              .map((column) => {
                                const value = getValueFromRow(row, column)
                                const content =
                                  column.format && value !== undefined ? column.format(value, row) : value
                                return (
                                  <Box key={column.id} sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                      {column.label}:
                                    </Typography>
                                    <Box
                                      sx={{
                                        minWidth: 0,
                                        fontSize: "0.875rem",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {content}
                                    </Box>
                                  </Box>
                                )
                              })}
                          </Box>
                        )}
                      </Paper>
                    )
                  })
                )}
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small" aria-label={`${title} table`}>
                  <TableHead>
                    <TableRow>
                      {visibleColumns.map((column) => (
  <TableCell
    key={column.id}
    align={column.align || "left"}
    style={{ minWidth: column.minWidth }}
    sortDirection={orderBy === column.id ? order : false}
  >
    <TableSortLabel
      active={orderBy === column.id}
      direction={orderBy === column.id ? order : "asc"}
      onClick={() => handleSort(column.id)}
    >
      {column.label}
    </TableSortLabel>
  </TableCell>
))}
                      {/* ✅ MELHORADO: Mostrar coluna de ações se houver pelo menos uma permissão */}
                      {(canView || canEdit || canDelete) && !customActions && <TableCell align="center">{t("common.actions")}</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={columns.length + ((canView || canEdit || canDelete) && !customActions ? 1 : 0)} align="center">
                          {emptyMessage || t("entity.emptyMessage")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                        return (
                          <TableRow
                            hover
                            tabIndex={-1}
                            key={getIdFromRow(row)}
                            onClick={() => handleRowClick(row)}
                            sx={onRowClick && canView ? { cursor: "pointer" } : undefined}
                          >
                            {visibleColumns.map((column) => {
                              const value = getValueFromRow(row, column)
                              return (
                                <TableCell key={column.id} align={column.align || "left"}>
                                  {column.format && value !== undefined ? column.format(value, row) : value}
                                </TableCell>
                              )
                            })}

                            {/* ✅ MELHORADO: Mostrar ações baseado nas permissões */}
                            {(canView || canEdit || canDelete) && !customActions && (
                              <TableCell align="center">
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                  {canView && (
                                    <Tooltip title={t("common.details")}>
                                      <IconButton
                                        color="info"
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          if (onRowClick) onRowClick(row)
                                        }}
                                      >
                                        <InfoIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {canEdit && (
                                    <Tooltip title={t("common.edit")}>
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={(e) => handleEdit(getIdFromRow(row), e)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {canDelete && (
                                    <Tooltip title={t("common.delete")}>
                                      <IconButton
                                        color="error"
                                        size="small"
                                        onClick={(e) => handleDeleteClick(getIdFromRow(row), e)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            )}

                            {(canView || canEdit || canDelete) && customActions && (
                              <TableCell align="right">{customActions(row)}</TableCell>
                            )}
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={isMobile ? t("entity.rowsPerPageShort") : t("entity.rowsPerPage")}
              labelDisplayedRows={({ from, to, count }) => t("entity.displayedRows", { from, to, count })}
            />
          </>
        )}
      </Paper>

      {debug &&
        (providedData ? (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6">Dados fornecidos externamente:</Typography>
            <Paper sx={{ p: 2, maxHeight: 300, overflow: "auto" }}>
              <pre>{JSON.stringify(providedData, null, 2)}</pre>
            </Paper>
          </Box>
        ) : (
          rawResponse && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6">Dados brutos da resposta:</Typography>
              <Paper sx={{ p: 2, maxHeight: 300, overflow: "auto" }}>
                <pre>{JSON.stringify(rawResponse, null, 2)}</pre>
              </Paper>

              <Typography variant="h6" sx={{ mt: 2 }}>
                Dados processados:
              </Typography>
              <Paper sx={{ p: 2, maxHeight: 300, overflow: "auto" }}>
                <pre>{JSON.stringify(data, null, 2)}</pre>
              </Paper>
            </Box>
          )
        ))}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t("entity.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{deleteConfirmationMessage || t("entity.deleteMessage")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t("common.cancel")}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )

  return containerProps ? <Container {...containerProps}>{containerComponent}</Container> : containerComponent
}
export default EntityList